import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import SellerPage from "./seller-page";
import { useSellerData } from "./useSellerData";

import { useNotifications, useUI, useProduct } from "../contexts";
import { trackError } from "../utils/error-tracking";
import { asError } from "../hooks/as-error";
import { isBackNavigation } from "../utils/navigation-direction";
import { recallBrowseList } from "../utils/browse-list-cache";
import { PAGE_SIZE } from "../data/js/pagination";
import type { Product } from '../utils/product/product-types';

interface PageProps {
  user?: Record<string, unknown>;
  currentList?: Record<string, unknown>[];
  [key: string]: unknown;
}

function Page(props: PageProps) {
  const router = useRouter();
  const { createNotification } = useNotifications();
  const { setProductOpen } = useUI();
  const { removeProduct, replaceCurrentList } = useProduct();
  const { fetchOtherListings, pickDataForListing } = useSellerData();
  // Initialize user from SSR props if available
  const [user, setUser] = useState(props.user || null);

  // Update local state if SSR props.user changes
  useEffect(() => {
    if (props.user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs SSR-provided user prop into local state when it changes; cannot derive because user is also set by the client-side fetch in getProps
      setUser(props.user);
    }
  }, [props.user]);
  const { slug, sold } = router.query;

  // Use refs for values needed in getProps but that shouldn't trigger re-creation
  const createNotificationRef = useRef(createNotification);
  const replaceCurrentListRef = useRef(replaceCurrentList);
  const routerRef = useRef(router);
  // Keep refs in sync with the latest values on every render (read later in getProps)
  useEffect(() => {
    createNotificationRef.current = createNotification;
    replaceCurrentListRef.current = replaceCurrentList;
    routerRef.current = router;
  });

  // Runs only from the mount effect below, which calls it when there is a slug.
  const getProps = useCallback(async (slugStr: string) => {
    // Pass slug directly - API handles both numeric IDs and usernames
    try {
      const soldParam = sold !== undefined ? String(sold) : undefined;
      const r = await fetchOtherListings(slugStr, null, 1, soldParam);
      const { listings, user: fetchedUser } = r;

      // Convert trustLevel to number since API returns it as string
      const trustLevel = Number(fetchedUser.trustLevel);

      if (trustLevel < 2 || listings.length < 1) {
        void routerRef.current.replace("/");
        createNotificationRef.current({type: "error", msg: "This seller was not found", dismissed: false})
        return;
      }

      // pickDataForListing yields the reshaped search row; Product is the UI's
      // view of that same wire object. The two are separate hand-written
      // descriptions (contexts/ProductProvider narrows the same boundary), so
      // the narrowing is stated at the call rather than hidden in an `any`.
      const formattedListings = pickDataForListing(listings, 32) as unknown as Product[];
      replaceCurrentListRef.current(formattedListings, { page: 1 });
      setUser(fetchedUser);
    } catch (_e) {
      trackError(asError(_e));
    }
  }, [sold, fetchOtherListings, pickDataForListing]);

  useEffect(() => {
    // Close product modal when navigating to seller page
    setProductOpen(false);
    removeProduct();

    // Back from a listing: the list the reader left, every page of it
    // (store-listing keeps it under the same key).
    const kept = isBackNavigation() ? recallBrowseList<Product>(router.asPath) : null;
    if (kept && kept.length > 0) {
      replaceCurrentList([...kept], { page: Math.ceil(kept.length / PAGE_SIZE.LISTINGS) });
      return;
    }

    // If we have SSR props, use them and skip client-side fetch
    if (props.currentList && props.currentList.length > 0) {
      const formattedListings = pickDataForListing(props.currentList, 32) as unknown as Product[];
      replaceCurrentList(formattedListings, { page: 1 });
      return;
    }

    // Only fetch client-side if we have NO SSR data at all
    if (slug && !props.user && !props.currentList) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- getProps is async: its setUser runs after the fetch resolves, not during this effect
      void getProps(String(slug));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, sold, props.currentList]); // Re-run when the seller (slug), the sold
  // filter, or the SSR list changes. `sold` matters on mobile (no SSR re-run):
  // getProps reads it, so a `?sold=` change must re-trigger the fetch.

  // Use SSR user from props if available, otherwise use client-side fetched user
  const finalUser = props.user || user;
  const maybeUser = finalUser ? { user: finalUser } : {};

  return <SellerPage {...props} {...maybeUser} />;
}

export default Page;
